import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

import { countries, getStatesByCountry } from '@/utils/locationData';

export interface EditLocationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { country: string; state: string; city: string }) => Promise<void>;
  initialData: { country: string; state: string; city: string };
}

export const EditLocationModal: React.FC<EditLocationModalProps> = ({ open, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [country, setCountry] = useState(initialData.country || '');
  const [state, setState] = useState(initialData.state || '');
  const [city, setCity] = useState(initialData.city || '');
  const [openCountry, setOpenCountry] = useState(false);
  const [openState, setOpenState] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // Find the country in the array by value (case-insensitive)
    const normalizedCountry = (initialData.country || '').toLowerCase();
    const matchedCountry = countries.find(c => c.value.toLowerCase() === normalizedCountry)?.value || '';
    // Find the state in the array by value (case-insensitive)
    const normalizedState = (initialData.state || '').toLowerCase();
    const matchedState = getStatesByCountry(matchedCountry).find(s => s.value.toLowerCase() === normalizedState)?.value || '';
    setCountry(matchedCountry);
    setState(matchedState);
    // If Brazil/USA, populate city, otherwise clear
    if (matchedCountry === 'brazil' || matchedCountry === 'usa') {
      setCity(initialData.city || '');
    } else {
      setCity('');
    }
  }, [initialData, open]);

  // When the country changes, reset state/city if necessary
  useEffect(() => {
    if (country === 'brazil' || country === 'usa') {
      // Keep state/city
    } else {
      setState('');
      setCity('');
    }
  }, [country]);

  const showStateCity = country === 'brazil' || country === 'usa';

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!country) errs.country = t('profile.country_required');
    if (showStateCity && !state) errs.state = t('profile.state_required');
    if (showStateCity && !city.trim()) errs.city = t('profile.city_required');
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      await onSave({ country, state: showStateCity ? state : '', city: showStateCity ? city : '' });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_location')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_location_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.country')}</label>
            <div className="w-full border border-black rounded-md relative">
              <Select value={country} onValueChange={v => { setCountry(v); setState(''); setCity(''); }} open={openCountry} onOpenChange={setOpenCountry}>
                <SelectTrigger className="w-full border-0 text-black bg-white relative rounded-md shadow-none focus:ring-0 focus:outline-none">
                  <SelectValue placeholder={t('profile.select_country')} />
                  {openCountry ? <ChevronUp className="absolute right-3 top-1/2 -translate-y-1/2 text-black" /> : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-black" />}
                </SelectTrigger>
                <SelectContent className="bg-white border-black">
                  {countries.map(c => (
                    <SelectItem key={c.value} value={c.value} className="text-black">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {errors.country && <p className="text-xs text-red-600 mt-1">{errors.country}</p>}
          </div>
          {showStateCity && (
            <div>
              <label className="block text-sm font-medium mb-1 text-black">{t('profile.state')}</label>
              <div className="w-full border border-black rounded-md relative">
                <Select value={state} onValueChange={setState} open={openState} onOpenChange={setOpenState}>
                  <SelectTrigger className="w-full border-0 text-black bg-white relative rounded-md shadow-none focus:ring-0 focus:outline-none">
                    <SelectValue placeholder={t('profile.select_state')} />
                    {openState ? <ChevronUp className="absolute right-3 top-1/2 -translate-y-1/2 text-black" /> : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-black" />}
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black">
                    {getStatesByCountry(country).map(s => (
                      <SelectItem key={s.value} value={s.value} className="text-black">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.state && <p className="text-xs text-red-600 mt-1">{errors.state}</p>}
            </div>
          )}
          {showStateCity && state && (
            <div>
              <label className="block text-sm font-medium mb-1 text-black">{t('profile.city')}</label>
              <Input value={city} onChange={e => setCity(e.target.value)} className="text-black border-black bg-white" />
              {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
            </div>
          )}
        </div>
        <Button
          className="w-full bg-sossiety-red hover:bg-sossiety-red-hover text-white font-medium py-3 h-11 rounded-full transition-colors duration-200 disabled:opacity-50 mt-6"
          onClick={handleSave}
          disabled={saving || Object.keys(validate()).length > 0}
        >
          {saving ? t('profile.saving') : t('common.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}; 